import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Landmark, Shield, ChartLine, Gavel, Users, MapPin, Flag, ListChecks, Compass,
  Network, Route, ScrollText, FlaskConical, Lightbulb, Map as MapIcon, TrendingUp,
  Beaker, Layers, Building, Globe, HandCoins, Handshake, Wallet, EyeOff,
  Eye, Database, FileText, FileCheck2, FileSignature, PlayCircle, Loader2,
  CheckCircle2, XCircle, Download, Sparkles, Award, Crown, ShieldAlert, Radio,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// ---------------------------------------------------------------------------
// Capability catalogue (mirrors backend)
// ---------------------------------------------------------------------------
const SECTIONS = [
  {
    id: "executive_government",
    label: "Executive Government",
    accent: "#7B2FBE",  // royal purple — executive accent
    items: [
      { key: "performance_index",       label: "Sovereign Performance Index™",     icon: Award },
      { key: "presidential_situation",  label: "Presidential Situation Room™",     icon: Crown },
      { key: "cabinet_command",         label: "Cabinet Command Centre™",          icon: ShieldAlert },
      { key: "executive_coordination",  label: "Executive Coordination Centre™",   icon: Radio },
      { key: "provincial_dashboards",   label: "Provincial Dashboards™",           icon: MapPin },
      { key: "national_risk_register",  label: "National Risk Register™",          icon: Shield },
      { key: "strategic_programme",     label: "Strategic Programme Tracker™",     icon: ListChecks },
      { key: "cabinet_decisions",       label: "Cabinet Decision Tracker™",        icon: Gavel },
      { key: "performance_observatory", label: "Performance Observatory™",         icon: ChartLine },
      { key: "priority_monitoring",     label: "Priority Monitoring™",             icon: Flag },
    ],
  },
  {
    id: "national_intelligence",
    label: "National Intelligence",
    accent: "#C99A2E",  // sovereign gold
    items: [
      { key: "knowledge_graph",       label: "National Knowledge Graph™",         icon: Network },
      { key: "value_chain",           label: "Value Chain Intelligence™",         icon: Route },
      { key: "policy_impact",         label: "Policy Impact Intelligence™",       icon: ScrollText },
      { key: "legislative_simulation",label: "Legislative Simulation Engine™",    icon: FlaskConical },
      { key: "innovation_observatory",label: "Innovation Observatory™",           icon: Lightbulb },
      { key: "digital_atlas",         label: "National Digital Atlas™",           icon: MapIcon },
      { key: "strategic_forecast",    label: "Strategic Forecast Centre™",        icon: TrendingUp },
      { key: "economic_scenario_lab", label: "Economic Scenario Laboratory™",     icon: Beaker },
      { key: "cross_sector",          label: "Cross-Sector Intelligence™",        icon: Layers },
      { key: "whole_of_government",   label: "Whole-of-Government Intelligence™", icon: Building },
    ],
  },
  {
    id: "international",
    label: "International Leadership",
    accent: "#4FA3D1",  // sovereign blue
    items: [
      { key: "reporting_centre",       label: "International Reporting Centre™",   icon: Globe },
      { key: "development_partner",    label: "Development Partner Dashboard™",    icon: Handshake },
      { key: "investment_intelligence",label: "National Investment Intelligence™", icon: HandCoins },
      { key: "sdg",                    label: "SDG Performance Centre™",           icon: Globe },
      { key: "au",                     label: "AU Reporting Centre™",              icon: Globe },
      { key: "sadc",                   label: "SADC Reporting Centre™",            icon: Globe },
      { key: "world_bank",             label: "World Bank Reporting™",             icon: Globe },
      { key: "imf",                    label: "IMF Reporting™",                    icon: Globe },
      { key: "oecd",                   label: "OECD Reporting™",                   icon: Globe },
      { key: "ifc",                    label: "IFC Reporting™",                    icon: Globe },
    ],
  },
  {
    id: "transparency",
    label: "Transparency",
    accent: "#6BB88A",  // sovereign green
    items: [
      { key: "public_portal",         label: "Public Transparency Portal™",   icon: Eye },
      { key: "citizen_dashboard",     label: "Citizen Resource Dashboard™",   icon: Users },
      { key: "open_data",             label: "National Open Data Exchange™",  icon: Database },
      { key: "public_executive",      label: "Public Executive Reports™",     icon: FileText },
      { key: "resource_transparency", label: "Resource Transparency™",        icon: Wallet },
    ],
  },
  {
    id: "executive_outputs",
    label: "Executive Outputs",
    accent: "#E67E7E",  // executive coral
    items: [
      { key: "brief_generator", label: "Executive Brief Generator™ (PDF + DOCX + QR)", icon: FileSignature },
    ],
  },
  {
    id: "governance",
    label: "Governance",
    accent: "#B3C2CC",
    items: [
      { key: "acceptance", label: "Acceptance & Non-Regression Audit", icon: FileCheck2 },
    ],
  },
];

const ALL_ITEMS = SECTIONS.flatMap((s) => s.items.map((it) => ({ ...it, sectionId: s.id, accent: s.accent })));

// ---------------------------------------------------------------------------
// GET endpoint map (delegates to backend)
// ---------------------------------------------------------------------------
const ENDPOINTS = {
  performance_index:       "/cadastre360/gsros/performance-index",
  presidential_situation:  "/cadastre360/gsros/presidential-situation-room",
  cabinet_command:         "/cadastre360/gsros/cabinet-command-centre",
  executive_coordination:  "/cadastre360/gsros/executive-coordination",
  provincial_dashboards:   "/cadastre360/gsros/provincial-dashboards",
  national_risk_register:  "/cadastre360/gsros/national-risk-register",
  strategic_programme:     "/cadastre360/gsros/strategic-programme-tracker",
  performance_observatory: "/cadastre360/gsros/performance-observatory",
  priority_monitoring:     "/cadastre360/gsros/priority-monitoring",
  knowledge_graph:         "/cadastre360/gsros/knowledge-graph",
  value_chain:             "/cadastre360/gsros/value-chain-intelligence",
  policy_impact:           "/cadastre360/gsros/policy-impact-intelligence",
  innovation_observatory:  "/cadastre360/gsros/innovation-observatory",
  digital_atlas:           "/cadastre360/gsros/digital-atlas",
  strategic_forecast:      "/cadastre360/gsros/strategic-forecast-centre",
  cross_sector:            "/cadastre360/gsros/cross-sector-intelligence",
  whole_of_government:     "/cadastre360/gsros/whole-of-government-intelligence",
  reporting_centre:        "/cadastre360/gsros/international-reporting",
  development_partner:     "/cadastre360/gsros/development-partner-dashboard",
  investment_intelligence: "/cadastre360/gsros/investment-intelligence",
  sdg:                     "/cadastre360/gsros/international-reporting/sdg",
  au:                      "/cadastre360/gsros/international-reporting/au",
  sadc:                    "/cadastre360/gsros/international-reporting/sadc",
  world_bank:              "/cadastre360/gsros/international-reporting/world_bank",
  imf:                     "/cadastre360/gsros/international-reporting/imf",
  oecd:                    "/cadastre360/gsros/international-reporting/oecd",
  ifc:                     "/cadastre360/gsros/international-reporting/ifc",
  public_portal:           "/cadastre360/gsros/transparency/public-portal",
  citizen_dashboard:       "/cadastre360/gsros/transparency/citizen-dashboard",
  open_data:               "/cadastre360/gsros/transparency/open-data",
  public_executive:        "/cadastre360/gsros/transparency/executive-reports",
  resource_transparency:   "/cadastre360/gsros/transparency/resource-transparency",
};

// ---------------------------------------------------------------------------
// Display primitives with executive accent
// ---------------------------------------------------------------------------
const KV = ({ k, v }) => (
  <div className="border-b border-white/5 pb-1 flex items-start gap-2 text-[12px]">
    <div className="font-mono uppercase tracking-[0.16em] text-[10px] text-[#7C93A5] flex-1 truncate">{k}</div>
    <div className="font-mono text-[#F6F8FA] break-all text-right max-w-[60%]">
      {typeof v === "object" ? JSON.stringify(v) : String(v)}
    </div>
  </div>
);

const Card = ({ title, accent = "#C99A2E", children, testId, right }) => (
  <div className="rounded-sm border" style={{ borderColor: accent + "40", background: "rgba(11,53,88,0.35)" }} data-testid={testId}>
    <div className="px-3 py-2 border-b" style={{ borderColor: accent + "30" }}>
      <div className="flex items-center gap-2">
        <div className="w-1 h-4 rounded-sm" style={{ background: accent }} />
        <div className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: accent }}>{title}</div>
        <div className="ml-auto">{right}</div>
      </div>
    </div>
    <div className="p-3 space-y-1">{children}</div>
  </div>
);

const Metric = ({ label, value, accent = "#C99A2E", testId }) => (
  <div className="rounded-sm border px-3 py-2" style={{ borderColor: accent + "30", background: "rgba(7,27,43,0.45)" }} data-testid={testId}>
    <div className="font-mono uppercase tracking-[0.22em] text-[10px] text-[#7C93A5]">{label}</div>
    <div className="font-display font-bold text-[#F6F8FA] mt-0.5 text-[15px] break-words">
      {typeof value === "boolean" ? (value ? "yes" : "no") : String(value)}
    </div>
  </div>
);

const AutoPanel = ({ data, accent = "#C99A2E" }) => {
  if (!data) return null;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {Object.entries(data).map(([key, val]) => {
        if (key === "generated_at") return null;
        if (Array.isArray(val)) {
          return (
            <Card key={key} title={key.replace(/_/g, " ")} accent={accent}>
              <div className="space-y-1 max-h-[360px] overflow-y-auto">
                {val.slice(0, 60).map((item, i) => (
                  <div key={key + "-" + i} className="border-b border-white/5 pb-1 text-[11px] text-[#DCE3E8]">
                    {typeof item === "object" && item !== null ? (
                      <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                        {Object.entries(item).slice(0, 12).map(([kk, vv]) => (
                          <div key={kk} className="truncate">
                            <span className="font-mono text-[10px] text-[#7C93A5]">{kk}</span>{" "}
                            <span className="text-[#F6F8FA]">
                              {typeof vv === "object" ? JSON.stringify(vv) : String(vv)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : String(item)}
                  </div>
                ))}
                {val.length > 60 && (
                  <div className="text-[10px] font-mono text-[#7C93A5]">…{val.length - 60} more</div>
                )}
              </div>
            </Card>
          );
        }
        if (typeof val === "object" && val !== null) {
          return (
            <Card key={key} title={key.replace(/_/g, " ")} accent={accent}>
              {Object.entries(val).slice(0, 40).map(([kk, vv]) => <KV key={kk} k={kk} v={vv} />)}
            </Card>
          );
        }
        return <Metric key={key} label={key.replace(/_/g, " ")} value={val} accent={accent} />;
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sovereign Performance Index — bespoke hero
// ---------------------------------------------------------------------------
const PerformanceIndexPanel = ({ accent }) => {
  const q = useQuery({
    queryKey: ["gsros-performance"],
    queryFn: async () => (await api.get(ENDPOINTS.performance_index)).data,
  });
  if (q.isLoading) return <div className="p-6 text-[#B3C2CC]">Loading sovereign index…</div>;
  const d = q.data || {};
  return (
    <div className="space-y-3" data-testid="gsros-performance-content">
      <div className="rounded-sm border p-6 flex flex-col md:flex-row gap-6 items-start"
            style={{ borderColor: accent + "60", background: `linear-gradient(135deg, rgba(7,27,43,0.9), rgba(123,47,190,0.15))` }}>
        <div>
          <div className="font-mono uppercase tracking-[0.28em] text-[10px]" style={{ color: accent }}>
            National Sovereign Performance Index
          </div>
          <div className="flex items-baseline gap-3 mt-2">
            <div className="font-display font-bold text-[#F6F8FA] leading-none" style={{ fontSize: 72 }}>
              {d.national_score ?? "—"}
            </div>
            <div className="rounded-sm px-2 py-1 text-[12px] font-mono uppercase tracking-[0.16em]"
                  style={{ background: accent + "30", color: accent }}>
              Grade {d.grade}
            </div>
          </div>
          <div className="mt-3 text-[13px] text-[#DCE3E8] max-w-3xl leading-relaxed italic border-l-2 pl-3"
                style={{ borderColor: accent }}>
            {d.ai_commentary}
          </div>
        </div>
        <div className="flex-1 min-w-[260px]">
          <div className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#7C93A5]">International comparison</div>
          <div className="mt-2 space-y-1">
            {Object.entries(d.international_comparison || {}).map(([c, s]) => (
              <div key={c} className="flex items-center gap-2 text-[12px]">
                <div className="w-32 truncate text-[#F6F8FA]">{c}</div>
                <div className="flex-1 h-1.5 rounded-sm bg-white/5 relative">
                  <div className="absolute inset-y-0 left-0 rounded-sm"
                        style={{ width: `${s}%`, background: c === "Zimbabwe" ? accent : "#4FA3D1" }} />
                </div>
                <div className="w-10 text-right font-mono text-[11px] text-[#F6F8FA]">{s}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card title="Dimensional scores" accent={accent}>
          {(d.dimensions || []).map((dim) => (
            <div key={dim.key} className="flex items-center gap-2 py-1 border-b border-white/5 text-[12px]">
              <div className="w-52 truncate text-[#F6F8FA]">{dim.key.replace(/_/g, " ")}</div>
              <div className="flex-1 h-1 rounded-sm bg-white/5 relative">
                <div className="absolute inset-y-0 left-0 rounded-sm" style={{ width: `${dim.score}%`, background: accent }} />
              </div>
              <div className="w-14 text-right font-mono text-[11px] text-[#F6F8FA]">{dim.score}</div>
              <div className="w-8 text-right font-mono text-[10px] text-[#7C93A5]">×{dim.weight}</div>
            </div>
          ))}
        </Card>
        <Card title="Provincial ranking" accent={accent}>
          {(d.provincial_scores || []).map((p) => (
            <div key={p.province} className="flex items-center gap-2 py-1 border-b border-white/5 text-[12px]">
              <div className="w-8 font-mono text-[11px] text-[#7C93A5]">#{p.rank}</div>
              <div className="flex-1 text-[#F6F8FA]">{p.province}</div>
              <div className="w-14 text-right font-mono text-[11px] text-[#F6F8FA]">{p.score}</div>
              <div className={"w-14 text-right font-mono text-[10px] " + (p.trend_yoy_pct >= 0 ? "text-[#6BB88A]" : "text-[#E67E7E]")}>
                {p.trend_yoy_pct >= 0 ? "+" : ""}{p.trend_yoy_pct}%
              </div>
            </div>
          ))}
        </Card>
      </div>
      <Card title="Historical trend (6 years)" accent={accent}>
        <div className="flex items-end gap-2 h-32">
          {(d.historical_trend || []).map((h) => (
            <div key={h.year} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full rounded-t-sm" style={{ height: `${h.score}%`, background: accent, opacity: 0.6 }} />
              <div className="font-mono text-[10px] text-[#F6F8FA]">{h.score}</div>
              <div className="font-mono text-[9px] text-[#7C93A5]">{h.year}</div>
            </div>
          ))}
        </div>
      </Card>
      <Card title="Executive recommendations" accent={accent}>
        {(d.executive_recommendations || []).map((r, i) => (
          <div key={`rec-${i}`} className="border-b border-white/5 pb-1 text-[12px] text-[#F6F8FA]">→ {r}</div>
        ))}
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Cabinet Command Centre — urgency-scored feed
// ---------------------------------------------------------------------------
const CabinetCommandPanel = ({ accent }) => {
  const q = useQuery({
    queryKey: ["gsros-cabinet-command"],
    queryFn: async () => (await api.get(ENDPOINTS.cabinet_command)).data,
  });
  if (q.isLoading) return <div className="p-6 text-[#B3C2CC]">Loading…</div>;
  const d = q.data || {};
  const urgencyColor = (n) => n >= 90 ? "#E67E7E" : n >= 75 ? "#C99A2E" : "#4FA3D1";
  return (
    <div className="space-y-3" data-testid="gsros-cabinet-command-content">
      <div className="rounded-sm border p-4 flex items-center gap-6 flex-wrap"
            style={{ borderColor: accent + "40", background: "rgba(11,53,88,0.35)" }}>
        <div>
          <div className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: accent }}>
            What requires Government attention today?
          </div>
          <div className="mt-1 font-display text-2xl font-bold text-[#F6F8FA]">
            {d.total_items_flagged} items · avg urgency {d.average_urgency_score}
          </div>
        </div>
      </div>
      <Card title="Priority items (urgency-scored)" accent={accent}>
        <div className="max-h-[520px] overflow-y-auto">
          {(d.top_priority_items || []).map((it) => (
            <div key={it.id} className="border-b border-white/5 py-2 flex items-start gap-3">
              <div className="rounded-sm px-2 py-0.5 font-mono text-[11px]"
                    style={{ background: urgencyColor(it.urgency_score) + "30", color: urgencyColor(it.urgency_score) }}>
                {it.urgency_score}
              </div>
              <div className="flex-1">
                <div className="text-[12.5px] text-[#F6F8FA] font-medium">{it.detail}</div>
                <div className="text-[10.5px] text-[#7C93A5] font-mono">
                  {it.kind} · {it.portfolio}
                </div>
                <div className="text-[11px] text-[#DCE3E8] mt-0.5">→ {it.recommended_action}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>
      <Card title="Portfolio breakdown" accent={accent}>
        {(d.portfolio_breakdown || []).map((p) => <KV key={p.portfolio} k={p.portfolio} v={p.items} />)}
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Cabinet Decision Tracker — list + create
// ---------------------------------------------------------------------------
const CabinetDecisionsPanel = ({ accent }) => {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["gsros-cabinet-decisions"],
    queryFn: async () => (await api.get("/cadastre360/gsros/cabinet-decisions")).data,
  });
  const [title, setTitle] = useState("");
  const [portfolio, setPortfolio] = useState("Mines & Mining Development");
  const [directive, setDirective] = useState("");
  const [priority, setPriority] = useState("high");
  const create = useMutation({
    mutationFn: async () => (await api.post("/cadastre360/gsros/cabinet-decisions", {
      title, portfolio, directive, priority,
    })).data,
    onSuccess: () => {
      toast.success("Cabinet decision recorded");
      setTitle(""); setDirective("");
      qc.invalidateQueries({ queryKey: ["gsros-cabinet-decisions"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Requires executive privilege"),
  });
  return (
    <div className="space-y-3" data-testid="gsros-cabinet-decisions-content">
      <Card title="Record cabinet decision" accent={accent}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Decision title"
                  data-testid="gsros-cd-title"
                  className="bg-[#0B3558]/40 border border-white/10 rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA]" />
          <input value={portfolio} onChange={(e) => setPortfolio(e.target.value)} placeholder="Portfolio"
                  data-testid="gsros-cd-portfolio"
                  className="bg-[#0B3558]/40 border border-white/10 rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA]" />
          <textarea value={directive} onChange={(e) => setDirective(e.target.value)} placeholder="Directive text"
                    data-testid="gsros-cd-directive"
                    className="md:col-span-2 bg-[#0B3558]/40 border border-white/10 rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA] min-h-[70px]" />
          <select value={priority} onChange={(e) => setPriority(e.target.value)}
                  data-testid="gsros-cd-priority"
                  className="bg-[#0B3558]/40 border border-white/10 rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA]">
            {["critical", "high", "medium", "low"].map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <button onClick={() => create.mutate()} disabled={create.isPending || !title || !directive}
                  data-testid="gsros-cd-create"
                  className="rounded-sm px-4 py-2 text-[11px] font-mono uppercase tracking-[0.16em] inline-flex items-center justify-center gap-1"
                  style={{ background: accent, color: "#0B0F14" }}>
            {create.isPending ? <Loader2 size={11} className="animate-spin" /> : <Gavel size={11} />} Record decision
          </button>
        </div>
      </Card>
      <Card title={`Recorded decisions (${list.data?.count ?? 0})`} accent={accent}>
        <div className="max-h-[420px] overflow-y-auto">
          {(list.data?.decisions || []).map((it) => (
            <div key={it.id} className="border-b border-white/5 py-1.5">
              <div className="flex items-center gap-2">
                <span className="rounded-sm px-1.5 py-0.5 text-[10px] font-mono uppercase"
                       style={{ background: accent + "30", color: accent }}>{it.priority}</span>
                <div className="text-[12px] text-[#F6F8FA] font-medium">{it.title}</div>
              </div>
              <div className="text-[10.5px] text-[#7C93A5] font-mono">
                {it.portfolio} · raised by {it.raised_by} · {it.raised_at?.slice(0, 19).replace("T", " ")}
              </div>
              <div className="text-[11px] text-[#DCE3E8]">{it.directive}</div>
            </div>
          ))}
          {(list.data?.decisions || []).length === 0 && (
            <div className="text-[11px] text-[#7C93A5] italic">No decisions recorded yet.</div>
          )}
        </div>
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Legislative Simulation
// ---------------------------------------------------------------------------
const LegislativeSimulationPanel = ({ accent }) => {
  const [billName, setBillName] = useState("Mining Royalty Amendment 2026");
  const [parameter, setParameter] = useState("royalty");
  const [change, setChange] = useState(3);
  const [horizon, setHorizon] = useState(5);
  const [notes, setNotes] = useState("");
  const [result, setResult] = useState(null);
  const run = useMutation({
    mutationFn: async () => (await api.post("/cadastre360/gsros/legislative-simulation", {
      bill_name: billName, parameter, change_pct: change, horizon_years: horizon, notes,
    })).data,
    onSuccess: (d) => { setResult(d); toast.success("Legislative impact simulated"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Requires executive privilege"),
  });
  return (
    <div className="space-y-3" data-testid="gsros-legislative-sim">
      <Card title="Legislative simulation" accent={accent}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input value={billName} onChange={(e) => setBillName(e.target.value)} placeholder="Bill name"
                  data-testid="gsros-ls-bill"
                  className="bg-[#0B3558]/40 border border-white/10 rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA]" />
          <select value={parameter} onChange={(e) => setParameter(e.target.value)}
                  data-testid="gsros-ls-parameter"
                  className="bg-[#0B3558]/40 border border-white/10 rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA]">
            {["royalty", "tax", "licence_fee", "local_content", "export_restriction", "environmental"].map((p) =>
              <option key={p} value={p}>{p.replace(/_/g, " ")}</option>)}
          </select>
          <input type="number" value={change} onChange={(e) => setChange(Number(e.target.value))} placeholder="Change %"
                  data-testid="gsros-ls-change"
                  className="bg-[#0B3558]/40 border border-white/10 rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA] font-mono" />
          <input type="number" value={horizon} onChange={(e) => setHorizon(Number(e.target.value))} placeholder="Horizon (years)"
                  data-testid="gsros-ls-horizon"
                  className="bg-[#0B3558]/40 border border-white/10 rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA] font-mono" />
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes"
                    data-testid="gsros-ls-notes"
                    className="md:col-span-2 bg-[#0B3558]/40 border border-white/10 rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA] min-h-[50px]" />
          <button onClick={() => run.mutate()} disabled={run.isPending}
                  data-testid="gsros-ls-run"
                  className="md:col-span-2 rounded-sm px-4 py-2 text-[11px] font-mono uppercase tracking-[0.16em] inline-flex items-center justify-center gap-1"
                  style={{ background: accent, color: "#0B0F14" }}>
            {run.isPending ? <Loader2 size={11} className="animate-spin" /> : <FlaskConical size={11} />} Simulate bill impact
          </button>
        </div>
      </Card>
      {result && (
        <>
          <div className="rounded-sm border p-4"
                style={{ borderColor: accent + "60", background: "rgba(7,27,43,0.65)" }}>
            <div className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: accent }}>Verdict</div>
            <div className="mt-1 font-display font-bold text-[#F6F8FA] text-lg">{result.verdict}</div>
          </div>
          <AutoPanel data={result} accent={accent} />
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Economic Scenario Lab
// ---------------------------------------------------------------------------
const EconomicScenarioLabPanel = ({ accent }) => {
  const [scenario, setScenario] = useState("Commodity Boom 2027");
  const [driver, setDriver] = useState("commodity_price");
  const [magnitude, setMagnitude] = useState(15);
  const [horizon, setHorizon] = useState(5);
  const [result, setResult] = useState(null);
  const run = useMutation({
    mutationFn: async () => (await api.post("/cadastre360/gsros/economic-scenario-lab", {
      scenario, driver, magnitude_pct: magnitude, horizon_years: horizon,
    })).data,
    onSuccess: (d) => { setResult(d); toast.success("Economic scenario simulated"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Requires executive privilege"),
  });
  return (
    <div className="space-y-3" data-testid="gsros-econ-lab">
      <Card title="Economic scenario laboratory" accent={accent}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input value={scenario} onChange={(e) => setScenario(e.target.value)} placeholder="Scenario name"
                  data-testid="gsros-econ-scenario"
                  className="bg-[#0B3558]/40 border border-white/10 rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA]" />
          <select value={driver} onChange={(e) => setDriver(e.target.value)}
                  data-testid="gsros-econ-driver"
                  className="bg-[#0B3558]/40 border border-white/10 rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA]">
            {["commodity_price", "inflation", "fx", "interest_rate", "investment", "discovery"].map((d) =>
              <option key={d} value={d}>{d.replace(/_/g, " ")}</option>)}
          </select>
          <input type="number" value={magnitude} onChange={(e) => setMagnitude(Number(e.target.value))} placeholder="Magnitude %"
                  data-testid="gsros-econ-magnitude"
                  className="bg-[#0B3558]/40 border border-white/10 rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA] font-mono" />
          <input type="number" value={horizon} onChange={(e) => setHorizon(Number(e.target.value))} placeholder="Horizon (years)"
                  data-testid="gsros-econ-horizon"
                  className="bg-[#0B3558]/40 border border-white/10 rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA] font-mono" />
          <button onClick={() => run.mutate()} disabled={run.isPending}
                  data-testid="gsros-econ-run"
                  className="md:col-span-2 rounded-sm px-4 py-2 text-[11px] font-mono uppercase tracking-[0.16em] inline-flex items-center justify-center gap-1"
                  style={{ background: accent, color: "#0B0F14" }}>
            {run.isPending ? <Loader2 size={11} className="animate-spin" /> : <Beaker size={11} />} Run scenario
          </button>
        </div>
      </Card>
      {result && <AutoPanel data={result} accent={accent} />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Executive Brief Generator with PDF/DOCX download
// ---------------------------------------------------------------------------
const BriefGeneratorPanel = ({ accent }) => {
  const qc = useQueryClient();
  const [audience, setAudience] = useState("cabinet");
  const [period, setPeriod] = useState("weekly");
  const [focus, setFocus] = useState("");
  const [current, setCurrent] = useState(null);
  const history = useQuery({
    queryKey: ["gsros-brief-history"],
    queryFn: async () => (await api.get("/cadastre360/gsros/brief-generator/history")).data,
  });
  const gen = useMutation({
    mutationFn: async () => (await api.post("/cadastre360/gsros/brief-generator", {
      audience, period, focus, include_ai_narrative: true,
    })).data,
    onSuccess: (d) => {
      setCurrent(d);
      toast.success("Executive briefing composed");
      qc.invalidateQueries({ queryKey: ["gsros-brief-history"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Requires executive privilege"),
  });
  return (
    <div className="space-y-3" data-testid="gsros-brief-generator">
      <Card title="Executive Brief Generator" accent={accent}
             right={
               <span className="font-mono text-[10px] text-[#7C93A5] uppercase tracking-[0.14em]">
                 PDF + DOCX + QR-signed
               </span>
             }>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <select value={audience} onChange={(e) => setAudience(e.target.value)}
                  data-testid="gsros-brief-audience"
                  className="bg-[#0B3558]/40 border border-white/10 rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA]">
            <option value="president">His Excellency, the President</option>
            <option value="cabinet">The Cabinet of Zimbabwe</option>
            <option value="minister">Honourable Minister</option>
            <option value="permanent_secretary">Permanent Secretary</option>
            <option value="provincial">Provincial Executive</option>
          </select>
          <select value={period} onChange={(e) => setPeriod(e.target.value)}
                  data-testid="gsros-brief-period"
                  className="bg-[#0B3558]/40 border border-white/10 rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA]">
            {["weekly", "monthly", "quarterly", "annual"].map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <input value={focus} onChange={(e) => setFocus(e.target.value)} placeholder="Focus (optional)"
                  data-testid="gsros-brief-focus"
                  className="bg-[#0B3558]/40 border border-white/10 rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA]" />
          <button onClick={() => gen.mutate()} disabled={gen.isPending}
                  data-testid="gsros-brief-generate"
                  className="md:col-span-3 rounded-sm px-4 py-2 text-[11px] font-mono uppercase tracking-[0.16em] inline-flex items-center justify-center gap-1"
                  style={{ background: accent, color: "#0B0F14" }}>
            {gen.isPending ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} Generate briefing pack
          </button>
        </div>
      </Card>
      {current && (
        <Card title={`Briefing · ${current.audience_title}`} accent={accent}
               right={
                 <div className="flex items-center gap-2">
                   <a href={API + `/cadastre360/gsros/brief-generator/${current.id}.pdf`}
                       target="_blank" rel="noreferrer"
                       data-testid="gsros-brief-download-pdf"
                       className="rounded-sm border border-white/20 px-2 py-1 text-[10px] font-mono uppercase inline-flex items-center gap-1 text-[#F6F8FA]">
                     <Download size={10} /> PDF
                   </a>
                   <a href={API + `/cadastre360/gsros/brief-generator/${current.id}.docx`}
                       target="_blank" rel="noreferrer"
                       data-testid="gsros-brief-download-docx"
                       className="rounded-sm border border-white/20 px-2 py-1 text-[10px] font-mono uppercase inline-flex items-center gap-1 text-[#F6F8FA]">
                     <Download size={10} /> DOCX
                   </a>
                 </div>
               }>
          <div className="text-[12.5px] text-[#F6F8FA] whitespace-pre-wrap leading-relaxed">
            {current.narrative}
          </div>
          <div className="mt-3 border-t border-white/10 pt-2">
            <div className="font-mono uppercase tracking-[0.16em] text-[10px] text-[#7C93A5]">Recommended actions</div>
            <div className="mt-1 space-y-0.5">
              {(current.recommended_actions || []).map((a, i) => (
                <div key={`a-${i}`} className="text-[11.5px] text-[#DCE3E8]">→ {a}</div>
              ))}
            </div>
          </div>
          <div className="mt-3 text-[10px] font-mono text-[#7C93A5] break-all">
            id: {current.id} · sha256: {current.digital_signature_sha256}
          </div>
        </Card>
      )}
      <Card title={`Recent briefings (${history.data?.length ?? 0})`} accent={accent}>
        <div className="max-h-[280px] overflow-y-auto space-y-1">
          {(history.data || []).map((b) => (
            <div key={b.id} className="border-b border-white/5 pb-1 text-[11px] flex items-center gap-2">
              <span className="rounded-sm px-1.5 py-0.5 text-[10px] font-mono uppercase" style={{ background: accent + "30", color: accent }}>
                {b.period}
              </span>
              <span className="text-[#F6F8FA] flex-1 truncate">{b.audience_title}</span>
              <span className="font-mono text-[10px] text-[#7C93A5]">{b.generated_at?.slice(0, 19).replace("T", " ")}</span>
              <a href={API + `/cadastre360/gsros/brief-generator/${b.id}.pdf`} target="_blank" rel="noreferrer"
                  className="rounded-sm border border-white/20 px-1.5 py-0.5 text-[9.5px] font-mono uppercase inline-flex items-center gap-1 text-[#F6F8FA]">
                <Download size={9} /> PDF
              </a>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Acceptance panel
// ---------------------------------------------------------------------------
const AcceptancePanel = ({ accent }) => {
  const qc = useQueryClient();
  const reports = useQuery({
    queryKey: ["gsros-acceptance"],
    queryFn: async () => (await api.get("/cadastre360/gsros/acceptance/reports")).data,
  });
  const run = useMutation({
    mutationFn: async () => (await api.post("/cadastre360/gsros/acceptance/run")).data,
    onSuccess: (d) => {
      toast.success(`GSROS ${d.overall} · ${d.passed}/${d.scope_size}`);
      qc.invalidateQueries({ queryKey: ["gsros-acceptance"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Requires GSROS administrator"),
  });
  const latest = (reports.data || [])[0];
  return (
    <div className="space-y-3" data-testid="gsros-acceptance-content">
      <div className="rounded-sm border p-4 flex flex-wrap items-center gap-3"
            style={{ borderColor: accent + "40", background: "rgba(11,53,88,0.35)" }}>
        <div className="text-[12px] text-[#B3C2CC] flex-1 min-w-[240px]">
          Verify Phase VIII zero-regression and additive-only architecture across all
          GSROS capabilities. Confirms Land, Mining and Survey pillars untouched and
          Sovereign Intelligence Centre (Phase VII) intact.
        </div>
        <button onClick={() => run.mutate()} disabled={run.isPending}
                data-testid="gsros-acceptance-run"
                className="rounded-sm px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.16em] inline-flex items-center gap-1"
                style={{ background: accent, color: "#0B0F14" }}>
          {run.isPending ? <Loader2 size={11} className="animate-spin" /> : <PlayCircle size={11} />} Run acceptance
        </button>
      </div>
      {latest && (
        <Card title={`Latest report — ${latest.overall}`} accent={accent}
               right={<span className="font-mono text-[10px] text-[#F6F8FA]">{latest.passed}/{latest.scope_size}</span>}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 max-h-[480px] overflow-y-auto">
            {(latest.checks || []).map((c, i) => (
              <div key={`gsros-chk-${latest.id}-${i}`} className="border-b border-white/5 pb-1 flex items-start gap-2">
                {c.ok ? <CheckCircle2 size={12} className="text-[#6BB88A] mt-0.5" /> : <XCircle size={12} className="text-[#E67E7E] mt-0.5" />}
                <div>
                  <div className="text-[11px] text-[#F6F8FA]">{c.label}</div>
                  <div className="text-[10px] text-[#7C93A5] font-mono">{c.section} · {c.note}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2 text-[10px] font-mono text-[#7C93A5] break-all">signature: {latest.signature}</div>
        </Card>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Root GSROS page
// ---------------------------------------------------------------------------
export const GSROS = () => {
  const [activeKey, setActiveKey] = useState("performance_index");
  const activeItem = ALL_ITEMS.find((i) => i.key === activeKey) || ALL_ITEMS[0];
  const section = SECTIONS.find((s) => s.id === activeItem.sectionId);
  const accent = section?.accent || "#C99A2E";

  const renderContent = () => {
    switch (activeKey) {
      case "performance_index":         return <PerformanceIndexPanel accent={accent} />;
      case "cabinet_command":           return <CabinetCommandPanel accent={accent} />;
      case "cabinet_decisions":         return <CabinetDecisionsPanel accent={accent} />;
      case "legislative_simulation":    return <LegislativeSimulationPanel accent={accent} />;
      case "economic_scenario_lab":     return <EconomicScenarioLabPanel accent={accent} />;
      case "brief_generator":           return <BriefGeneratorPanel accent={accent} />;
      case "acceptance":                return <AcceptancePanel accent={accent} />;
      default:
        return <GenericGET endpoint={ENDPOINTS[activeKey]} accent={accent} keyName={activeKey} />;
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="page-gsros">
      {/* Executive hero */}
      <div className="rounded-sm border p-6 flex items-start gap-4 relative overflow-hidden"
            style={{
              borderColor: "#7B2FBE60",
              background: "linear-gradient(120deg, rgba(7,27,43,0.95) 0%, rgba(11,53,88,0.85) 50%, rgba(123,47,190,0.25) 100%)",
            }}>
        <Landmark size={38} className="text-[#C99A2E] mt-1" />
        <div className="flex-1">
          <div className="font-mono uppercase tracking-[0.32em] text-[10px] text-[#C99A2E]">
            Cadastre360™ · Phase VIII · Government Sovereign Resource Operating System v1.8
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[#F6F8FA] mt-1">
            GSROS™ — Executive Orchestration Layer
          </h1>
          <div className="text-[13px] text-[#DCE3E8] mt-2 max-w-4xl leading-relaxed">
            The executive orchestration layer sitting above the operational Land,
            Mining and Survey Cadastres. GSROS consumes existing enterprise services
            and produces sovereign national intelligence, executive decision support,
            international reporting and cabinet-grade briefings.
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {SECTIONS.slice(0, 5).map((s) => (
              <span key={s.id} className="rounded-sm px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.16em]"
                     style={{ background: s.accent + "20", color: s.accent, border: `1px solid ${s.accent}40` }}>
                {s.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Section navigation */}
      <div className="rounded-sm border border-white/10 p-2 space-y-1 sticky top-2 z-10"
            style={{ background: "rgba(7,27,43,0.85)", backdropFilter: "blur(12px)" }}
            data-testid="gsros-nav">
        {SECTIONS.map((s) => (
          <div key={s.id} className="flex flex-wrap items-center gap-1">
            <div className="font-mono uppercase tracking-[0.16em] text-[10px] mr-2 py-1"
                  style={{ color: s.accent, minWidth: 148 }}>
              {s.label}
            </div>
            {s.items.map((it) => {
              const Icon = it.icon;
              const active = activeKey === it.key;
              return (
                <button key={it.key} onClick={() => setActiveKey(it.key)}
                        data-testid={"gsros-tab-" + it.key}
                        className="px-2 py-1 rounded-sm text-[10.5px] font-mono uppercase tracking-[0.1em] inline-flex items-center gap-1"
                        style={{
                          background: active ? s.accent + "40" : "transparent",
                          color: active ? "#F6F8FA" : "#B3C2CC",
                          border: active ? `1px solid ${s.accent}` : "1px solid transparent",
                        }}>
                  <Icon size={10} style={{ color: s.accent }} /> {it.label}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Active header */}
      <div className="rounded-sm border p-3 flex items-center gap-3"
            style={{ borderColor: accent + "40", background: "rgba(11,53,88,0.4)" }}
            data-testid="gsros-active-header">
        <activeItem.icon size={20} style={{ color: accent }} />
        <div className="font-display text-lg font-bold text-[#F6F8FA]">{activeItem.label}</div>
        <span className="ml-auto rounded-sm px-2 py-0.5 text-[10px] font-mono uppercase" style={{ background: accent + "20", color: accent }}>
          {section?.label}
        </span>
      </div>

      {renderContent()}
    </div>
  );
};

// Generic GET fallback
const GenericGET = ({ endpoint, accent, keyName }) => {
  const q = useQuery({
    queryKey: ["gsros-generic-" + keyName],
    queryFn: async () => (await api.get(endpoint)).data,
    enabled: Boolean(endpoint),
  });
  if (!endpoint) return <div className="p-6 text-[#B3C2CC]">Capability presented via UI only.</div>;
  if (q.isLoading) return <div className="p-6 text-[#B3C2CC]">Loading…</div>;
  if (q.isError)   return <div className="p-6 text-[#E67E7E]">Unavailable</div>;
  return <AutoPanel data={q.data} accent={accent} />;
};

export default GSROS;
